/*
 * Editorial launch sections — motion for editorial-hero, editorial-shortlist,
 * editorial-story and editorial-panels. One file, loaded (deferred) by each section; it inits
 * once and re-inits a section when the theme editor reloads it.
 *
 *   [data-el-reveal="fade|up|stagger"]  entrance on scroll into view
 *   [data-el-hero]                      full-bleed → framed scroll reveal (desktop)
 *   [data-el-story]                     chapter mid-screen → its image crossfades in
 *   [data-el-panels]                    panels take turns widening (desktop auto-play)
 *
 * Everything degrades to the static stacked layout: with no JS, under
 * prefers-reduced-motion, and below 768px.
 */
(function () {
  if (window.__editorialLaunch) return;
  window.__editorialLaunch = true;

  var desktop = window.matchMedia('(min-width: 768px)');
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Sticky header offset ----------
     The theme's sticky header (+ announcement bar) covers the top of the
     viewport. Expose its height as --el-header-offset so pinned layouts
     (hero stage, story image) sit below it. */
  function headerOffset() {
    var h = document.querySelector('.site-header');
    var top = h ? Math.max(0, Math.round(h.getBoundingClientRect().bottom)) : 0;
    if (top > window.innerHeight * 0.3) top = 0;
    document.documentElement.style.setProperty('--el-header-offset', top + 'px');
    return top;
  }

  /* ---------- Entrance reveals ---------- */
  function initReveals(root) {
    var els = root.querySelectorAll('[data-el-reveal]');
    if (!els.length) return;
    if (reduced.matches || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('el-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('el-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    els.forEach(function (el) {
      el.classList.add('el-armed');
      if (el.dataset.elReveal === 'stagger') {
        Array.prototype.forEach.call(el.children, function (c, i) {
          c.style.transitionDelay = (i * 100) + 'ms';
        });
      }
      io.observe(el);
    });
  }

  /* ---------- Hero: full-bleed photo shrinks into the frame ---------- */
  function initHero(hero) {
    if (!hero.classList.contains('el-hero--reveal')) return;
    var track = hero.querySelector('[data-el-hero-track]');
    var stage = hero.querySelector('[data-el-hero-stage]');
    var cover = hero.querySelector('[data-el-hero-cover]');
    var frame = hero.querySelector('[data-el-hero-frame]');
    var head = hero.querySelector('[data-el-hero-head]');
    var foot = hero.querySelector('[data-el-hero-foot]');
    if (!track || !stage || !cover || !frame) return;

    var ticking = false;

    // The theme's sticky header (+ announcement bar) sits over the top of the
    // viewport — pin the stage below it and size it to the space that's left.
    function measure() {
      var top = headerOffset();
      hero.style.setProperty('--el-top', top + 'px');
      hero.style.setProperty('--el-avail', (window.innerHeight - top) + 'px');
    }

    function arm() {
      var on = desktop.matches && !reduced.matches;
      if (on) measure();
      hero.classList.toggle('is-armed', on);
      if (on) { update(); } else { cover.removeAttribute('style'); }
    }

    function update() {
      ticking = false;
      if (!hero.classList.contains('is-armed')) return;
      var tr = track.getBoundingClientRect();
      var span = tr.height - window.innerHeight;
      var p = span > 0 ? Math.min(Math.max(-tr.top / span, 0), 1) : 1;
      var t = Math.min(p / 0.7, 1);
      t = 1 - Math.pow(1 - t, 2); // ease-out

      var sr = stage.getBoundingClientRect();
      var fr = frame.getBoundingClientRect();
      var fx = fr.left - sr.left, fy = fr.top - sr.top;

      cover.style.left = (fx * t) + 'px';
      cover.style.top = (fy * t) + 'px';
      cover.style.width = (sr.width + (fr.width - sr.width) * t) + 'px';
      cover.style.height = (sr.height + (fr.height - sr.height) * t) + 'px';

      if (head) head.classList.toggle('is-in', p >= 0.55);
      if (foot) foot.classList.toggle('is-in', p >= 0.65);
    }

    function onScroll() {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { if (hero.classList.contains('is-armed')) measure(); onScroll(); });
    if (desktop.addEventListener) {
      desktop.addEventListener('change', arm);
      reduced.addEventListener('change', arm);
    }
    arm();
  }

  /* ---------- Story: active chapter drives the pinned image ---------- */
  function initStory(story) {
    var chapters = story.querySelectorAll('[data-el-story-chapter]');
    var imgs = story.querySelectorAll('[data-el-story-img]');
    if (!chapters.length) return;

    function activate(i) {
      chapters.forEach(function (c) { c.classList.toggle('is-active', c.dataset.elStoryChapter === String(i)); });
      imgs.forEach(function (im) { im.classList.toggle('is-on', im.dataset.elStoryImg === String(i)); });
    }
    activate(0);

    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) activate(e.target.dataset.elStoryChapter);
      });
    }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
    chapters.forEach(function (c) { io.observe(c); });
  }

  /* ---------- Panels: take turns widening; hover takes over ----------
     Desktop only (the row is a swipe carousel below 768px). The timer skips
     beats while the row is off screen, the tab is hidden or a panel is
     hovered, and stops for good once the section is removed by the editor. */
  function initPanels(row) {
    var panels = Array.prototype.slice.call(row.querySelectorAll('[data-el-panel]'));
    if (panels.length < 2) return;
    var interval = parseInt(row.dataset.elInterval, 10) || 4000;
    var current = 0;
    var held = false;
    var visible = true;

    function activate(n) {
      current = n;
      panels.forEach(function (p, k) { p.classList.toggle('is-active', k === n); });
    }
    function restartBar() {
      // Re-trigger the progress-bar animation on the newly active panel.
      row.classList.remove('is-playing');
      void row.offsetWidth;
      row.classList.add('is-playing');
    }

    panels.forEach(function (p, k) {
      p.addEventListener('mouseenter', function () { held = true; row.classList.add('is-held'); activate(k); });
      p.addEventListener('focusin', function () { held = true; row.classList.add('is-held'); activate(k); });
    });
    row.addEventListener('mouseleave', function () { held = false; row.classList.remove('is-held'); restartBar(); });

    if (!desktop.matches || reduced.matches) return;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }, { threshold: 0.25 }).observe(row);
    }

    restartBar();
    var timer = setInterval(function () {
      if (!row.isConnected) { clearInterval(timer); return; }
      if (held || !visible || document.hidden) return;
      activate((current + 1) % panels.length);
      restartBar();
    }, interval);
  }

  function init(root) {
    initReveals(root);
    root.querySelectorAll('[data-el-hero]').forEach(initHero);
    root.querySelectorAll('[data-el-story]').forEach(initStory);
    root.querySelectorAll('[data-el-panels]').forEach(initPanels);
  }

  function boot() {
    headerOffset();
    init(document);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders a section's markup on every setting change.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
